import type { ForwardedRef, MutableRefObject } from 'react';
import { forwardRef, useRef, useState, useEffect } from 'react';
import { css } from '@emotion/react';

import { mergeRefs } from '@utils/mergeRefs';
import { mergeEventHandlers } from '@utils/mergeEventHandlers';
import { useInputRefValueSetter } from '@hooks/useInputRefValueSetter';
import { ClearAction } from '@components/Actions/ClearAction/ClearAction';
import type { ClearActionProps } from '@components/Actions/ClearAction/ClearAction';

import type { SpecificInputProps } from '../BaseInput';
import { BaseInput } from '../BaseInput';

export interface SearchInputProps
  extends Omit<SpecificInputProps, 'icon' | 'actionContent'> {
  /**
   * Additional options for the clear action.
   * @see ClearActionProps
   */
  clearActionProps?: ClearActionProps;
}

/**
 * Hide browser search cancel button for those that support it.
 * (Will be replaced with our own implementation.)
 */
const searchInputStyles = css`
  &::-webkit-search-cancel-button {
    appearance: none;
  }
`;

/**
 * Props for the ClearSearch action
 * @see ClearSearch
 * @private
 */
interface ClearSearchProps extends Omit<ClearActionProps, 'onClick'> {
  /**
   * A ref to the associated input.
   */
  inputRef: MutableRefObject<HTMLInputElement>;
  /**
   * Make onClick event handler required.
   */
  onClick: ClearActionProps['onClick'];
}

/**
 * ClearSearch is used specifically in SearchInput. It is defined as a local
 * component because its behavior is tied strictly to SearchInput.
 * @see SearchInput
 * @private
 */
const ClearSearch = ({
  inputRef,
  onClick,
  ...props
}: ClearSearchProps): JSX.Element => {
  const setValue = useInputRefValueSetter(inputRef);

  return (
    <ClearAction
      {...props}
      onClick={mergeEventHandlers(() => {
        setValue('');
      }, onClick)}
    />
  );
};

/**
 * A search input allows users to enter search text.
 *
 * {@link https://zeroheight.com/76c439891/p/96722a-search-input Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-searchinput Code documentation}
 *
 * @example
 * <SearchInput />
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    { inputStyles, clearActionProps, onChange, ...props }: SearchInputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ): JSX.Element => {
    const inputRef = useRef<HTMLInputElement>();
    const [showClear, setShowClear] = useState(
      !!props.value || !!props.defaultValue,
    );

    useEffect(() => {
      setShowClear(!!props.value || !!props.defaultValue);
    }, [props.value, props.defaultValue]);

    // set a default description, only if clearActionProps is not provided.
    const { onClick: onClear, ...clearProps } = Object.assign(
      { description: 'Clear search' },
      clearActionProps,
    );

    return (
      <BaseInput
        {...props}
        type="search"
        icon="search"
        ref={mergeRefs([ref, inputRef])}
        inputStyles={[inputStyles, searchInputStyles]}
        actionContent={
          showClear && (
            <ClearSearch
              {...clearProps}
              inputRef={inputRef}
              onClick={mergeEventHandlers(() => {
                setShowClear(false);
                inputRef.current.focus();
              }, onClear)}
            />
          )
        }
        onChange={mergeEventHandlers(() => {
          setShowClear(!!inputRef.current.value);
        }, onChange)}
      />
    );
  },
);

SearchInput.displayName = 'SearchInput';

SearchInput.defaultProps = {
  isDisabled: false,
  isInvalid: false,
  isRequired: false,
  isReadonly: false,
  size: 'm',
};
